import type { DocCustomPropertyInfo } from '@affine/core/modules/db';

import { SystemPropertyTypes } from '../system-property-types';

/**
 * Mind-map-only build: doc-centric properties that have no meaning for a mind
 * map are hidden from every property list (filter menu, display/group/order
 * menus, and the doc properties panel).
 *
 * These are only hidden from the UI — the entries stay registered in
 * SystemPropertyTypes / WorkspacePropertyTypes so any existing doc that still
 * carries one of these properties can be read without a lookup failure.
 */
const HIDDEN_PROPERTY_TYPES = new Set([
  'journal', // journals are removed from this build
  'template', // templates are removed from this build
  'docPrimaryMode', // every doc is an edgeless mind map now
  'pageWidth', // page-document layout setting
  'integrationType', // calendar/journal integrations
]);

const systemProperties = Object.entries(SystemPropertyTypes).filter(
  ([type]) => !HIDDEN_PROPERTY_TYPES.has(type)
);

/**
 * In AFFiNE's property system, the property list can be fully customized by users.
 * For example, system properties like `createdAt` and `updatedAt`.
 * Users can completely remove them or create multiple instances. (This doesn't affect the underlying data, only the property display)
 *
 * To prevent user-defined properties from affecting the display of system properties, we've designed a dedicated property list for the Explorer.
 * This list generates a final property list based on system properties and user-defined properties, arranged in a specific order.
 *
 * For example, we have a workspace property list:
 *
 * - `{name: 'Birth', type: 'createdAt'}`
 * - `{name: 'Labels', type: 'tags'}`
 * - `{name: 'Name', type: 'Text'}`
 *
 * Assuming we have 3 system properties: `createdAt`, `updatedAt`, and `tags`
 *
 * The final property list should be:
 *
 * - `{systemProperty: {type: 'createdAt'}, workspaceProperty: {name: 'Birth'}}`
 * - `{systemProperty: {type: 'updatedAt'}, workspaceProperty: null}`
 * - `{systemProperty: {type: 'tags'}, workspaceProperty: {name: 'Labels'}}`
 * - `{systemProperty: null, workspaceProperty: {name: 'Name'}}`
 *
 * When displaying the list to users, we prioritize showing the workspace property if it exists, otherwise we show the system property.
 *
 * When users configure a property, we prioritize recording the system property's ID. This ensures that when users delete a property, it won't affect these settings.
 */
export function generateExplorerPropertyList(
  workspaceProperties: DocCustomPropertyInfo[]
): {
  systemProperty?: (typeof SystemPropertyTypes)[number] & { type: string };
  workspaceProperty?: DocCustomPropertyInfo;
}[] {
  const finalList = [];
  // Also drop workspace-defined properties of the hidden types (the default
  // workspace seeds Journal / Doc mode / Template / Page width properties).
  workspaceProperties = workspaceProperties.filter(
    p => !HIDDEN_PROPERTY_TYPES.has(p.type)
  );

  for (const [type, info] of systemProperties) {
    const workspacePropertyIndex = workspaceProperties.findIndex(
      p => p.type === type
    );
    if (workspacePropertyIndex === -1) {
      finalList.push({
        systemProperty: { ...info, type },
      });
    } else {
      finalList.push({
        systemProperty: { ...info, type },
        workspaceProperty: workspaceProperties[workspacePropertyIndex],
      });
      workspaceProperties.splice(workspacePropertyIndex, 1);
    }
  }

  for (const workspaceProperty of workspaceProperties) {
    finalList.push({
      workspaceProperty,
    });
  }

  return finalList;
}
